<template>
  <!-- Loading + 视频阶段 -->
  <the-loading
    v-if="phase === 'loading'"
    :data-ready="dataReady"
    @complete="handleLoadingComplete"
  />
  <!-- 主内容 -->
  <div class="app-container" v-else>
    <!-- 首屏 -->
    <the-overview @login="openLogin" />
    <!-- 回归福利 -->
    <template v-if="activeTab === 1">
      <!-- 礼包领取 -->
      <the-welfare
        ref="welfare"
        :welfare-config="welfareConfig"
        @login="openLogin"
      />
      <!-- 福利展示 -->
      <the-welfare-gallery
        :welfare-config="welfareConfig"
        :welfare-content-list="welfareContentList"
      />
    </template>
    <!-- 限定大奖 -->
    <template v-if="activeTab === 2">
      <!-- 抽奖 -->
      <the-lottery
        :rules-config="rulesConfig"
        @login="openLogin"
        @go-tasks="goTasks"
        @show-prize-records="showPrizeRecords"
        @bind-address="showBindAddress"
      />
      <!-- 任务 -->
      <the-tasks
        ref="tasks"
        :invite-text-list="inviteTextList"
        :rules-config="rulesConfig"
        @login="openLogin"
        @go-welfare="goWelfare"
        @refresh-user="initUser"
        @show-prize-records="showPrizeRecords"
      />
      <!-- 写真图集 -->
      <the-gallery :data-source="galleryList" />
    </template>
    <!-- 官方入口 -->
    <the-community :data-source="communityList" />
    <!-- 登录 -->
    <the-login ref="login" @login-success="handleLoginSuccess" />
    <!-- 获奖记录 -->
    <the-prize-records ref="prizeRecords" @bind-address="showBindAddress" />
    <!-- 邮寄地址 -->
    <the-bind-address ref="bindAddress" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TheOverview from "@/components/TheOverview";
import TheCommunity from "@/components/TheCommunity";
import TheGallery from "@/components/TheGallery";
import TheLottery from "@/components/TheLottery";
import TheTasks from "@/components/TheTasks";
import TheWelfare from "@/components/TheWelfare";
import TheWelfareGallery from "@/components/TheWelfareGallery";
import TheLogin from "@/components/TheLogin";
import ThePrizeRecords from "@/components/ThePrizeRecords";
import TheBindAddress from "@/components/TheBindAddress";
import TheLoading from "@/components/TheLoading";
import {
  fetchConfig,
  fetchLocalContent,
  fetchUserInfo,
  fetchSigninInfo
} from "@/api";
import { getQueryString } from "@/utils";

export default {
  name: "App",
  components: {
    TheOverview,
    TheCommunity,
    TheGallery,
    TheLottery,
    TheTasks,
    TheWelfare,
    TheWelfareGallery,
    TheLogin,
    ThePrizeRecords,
    TheBindAddress,
    TheLoading
  },
  data() {
    return {
      phase: "loading", // 'loading' | 'main'
      dataReady: false, // API 数据是否就绪
      communityList: [],
      galleryList: [],
      inviteTextList: [],
      welfareConfig: {},
      welfareContentList: [],
      rulesConfig: {}
    };
  },
  computed: {
    ...mapGetters(["activeTab", "isDebug", "token"])
  },
  async mounted() {
    // 预加载 API 数据
    await Promise.all([this.initConfig(), this.initLocal()]);
    this.dataReady = true;
    // 初始化用户
    if (this.token) {
      this.initUser();
    } else {
      this.setUser({});
    }
  },
  methods: {
    ...mapActions([
      "setConfig",
      "setUser",
      "updateAppData",
      "setActiveTab",
      "setServerTime",
      "setSigninInfo"
    ]),
    async initConfig() {
      const { data } = await fetchConfig();
      this.setConfig(data || {});
    },
    async initLocal() {
      const { data, time } = await fetchLocalContent({
        column_id: "195,196,193,194,197,198"
      });
      // 存储服务端时间
      this.setServerTime(time);

      // 官方入口列表
      const communityList = data[195] || [];
      this.communityList = communityList.map(el => el.content);

      // 写真图集列表
      const galleryList = data[196] || [];
      this.galleryList = galleryList.map(el => el.content);

      // 邀请文案列表
      const inviteTextList = data[197] || [];
      this.inviteTextList = inviteTextList.map(
        el => el.content && el.content.desc
      );

      // 回归福利配置
      this.welfareConfig =
        (data[193] && data[193][0] && data[193][0].content) || {};

      // 回归福利内容列表
      const welfareContentList = data[194] || [];
      this.welfareContentList = welfareContentList.map(el => el.content);

      // 规则奖励配置
      this.rulesConfig =
        (data[198] && data[198][0] && data[198][0].content) || {};
    },
    async initUser() {
      const { data = {} } = await fetchUserInfo();
      if (Object.keys(data).length > 0) {
        // 设置用户基本信息
        this.setUser(data);
        // 设置活动相关数据
        this.updateAppData(data);
        // 初始化签到信息
        this.initSignin();
        // 设置前端统计用户名
        if (this.$bl) {
          this.$bl.setConfig({
            setUsername: () => {
              return String(data.phone);
            }
          });
        }
      }
    },
    async initSignin() {
      const { data = {} } = await fetchSigninInfo(
        this.isDebug && { test_time: getQueryString("t") }
      );
      if (Object.keys(data).length > 0) {
        this.setSigninInfo(data);
      }
    },
    /* 登录 */
    openLogin(loginType) {
      this.$refs.login.show(loginType || 1);
    },
    /* 打开获奖记录 */
    showPrizeRecords() {
      this.$refs.prizeRecords.show();
    },
    /**
     * 登录后回调
     * @param {number} loginType 场景来源：1 仅登录, 2 预约领礼包
     **/
    handleLoginSuccess(loginType) {
      // 初始化签到信息
      this.initSignin();
      // 登录后立即领礼包
      if (loginType === 2) {
        this.$refs.welfare.reserveGift();
      }
    },
    /** 跳转到回归福利选项卡并定位到礼包内容 */
    goWelfare() {
      this.setActiveTab(1);
      this.$nextTick(() => {
        this.$refs.welfare.$refs.welfareContent.scrollIntoView();
      });
    },
    /** 定位到任务模块 */
    goTasks() {
      this.$refs.tasks.$el.scrollIntoView({ behavior: "smooth" });
    },
    /** 显示邮寄地址弹窗 */
    showBindAddress() {
      this.$refs.bindAddress.show();
    },
    /** Loading + 视频全部完成，进入主内容 */
    handleLoadingComplete(tab) {
      if (tab) {
        this.setActiveTab(tab);
      }
      this.phase = "main";
    }
  }
};
</script>
